import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Compass, Building2, Map, HardHat, FileSearch, ClipboardList, PenLine, CheckSquare, SearchCheck, Upload, Gift } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { Btn } from "./Bits";
import { Logo } from "./Logo";
import { CATEGORIES } from "@/data/services";

const SOFTWARE = ["AutoCAD", "Civil 3D", "Carlson Survey", "MicroStation", "QGIS", "ReCap / point clouds", "LandXML"];
const SHORT = { property: "Property & Boundary", topo: "Topographic", construction: "Construction", roads: "Roads & Routes", utilities: "Utilities", development: "Land Development", specialized: "Specialized & Digital" };
const SECTORS = [
  [Compass, "Land surveying firms", "Field-to-finished drafting capacity and overflow support in your own sheet standards."],
  [Building2, "Civil engineering practices", "Consistent survey base drawings and existing-conditions sheets for design teams."],
  [Map, "Mapping & GIS companies", "Survey data converted into organised CAD deliverables, surfaces and feature layers."],
  [HardHat, "Developers & builders", "Site, boundary, lot layout and as-built drafting from the surveyor's supplied data."],
];
const STAGES = [
  [FileSearch, "Input analysis", "Review coordinates, notes, deeds, plats and your CAD template before anything is drawn."],
  [ClipboardList, "Scope & quote", "Confirm deliverables, standards, timeline and price — in writing."],
  [PenLine, "Drafting to your standard", "Linework, labels and sheets built in your layers, blocks and title block."],
  [CheckSquare, "Drafter self-check", "The drafter checks the sheet against the job checklist and source data."],
  [SearchCheck, "Independent QA review", "A second technician verifies linework, labels and values before plot."],
  [Upload, "Delivery & revisions", "DWG / DXF / PDF delivered; revisions handled within the agreed scope."],
];

/* Orbiting rings of software + survey categories around the GEO DRAFTING mark */
const Orbit = ({ paused }) => {
  const reduce = useReducedMotion();
  const stop = paused || reduce;
  const ring = (items, r, dur, reverse) => (
    <motion.g animate={stop ? { rotate: 0 } : { rotate: reverse ? -360 : 360 }} transition={stop ? { duration: 0.6 } : { duration: dur, repeat: Infinity, ease: "linear" }} style={{ originX: "250px", originY: "250px" }}>
      <circle cx="250" cy="250" r={r} fill="none" stroke="#5AA9E6" strokeOpacity=".3" strokeDasharray="2 6" />
      {items.map((t, i) => {
        const a = (i / items.length) * Math.PI * 2, x = 250 + Math.cos(a) * r, y = 250 + Math.sin(a) * r;
        return (
          <motion.g key={t} animate={stop ? { rotate: 0 } : { rotate: reverse ? 360 : -360 }} transition={stop ? { duration: 0.6 } : { duration: dur, repeat: Infinity, ease: "linear" }} style={{ originX: `${x}px`, originY: `${y}px` }}>
            <rect x={x - t.length * 3.3 - 10} y={y - 11} width={t.length * 6.6 + 20} height="22" fill="#0A1D30" stroke="#5AA9E6" strokeOpacity=".4" />
            <circle cx={x - t.length * 3.3 - 2} cy={y} r="2" fill="#D8B66A" />
            <text x={x + 4} y={y + 3.5} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#dbe6f1">{t}</text>
          </motion.g>
        );
      })}
    </motion.g>
  );
  return (
    <svg viewBox="-40 -40 580 580" className="h-full w-full overflow-visible" role="img" aria-label="Software platforms and survey categories around GEO DRAFTING">
      <path d="M250 -40v580M-40 250h580" stroke="#5AA9E6" strokeOpacity=".15" strokeDasharray="3 6" />
      {ring(SOFTWARE, 125, 60, false)}
      {ring(CATEGORIES.map((c) => `${c.code} · ${SHORT[c.id]}`), 205, 90, true)}
      <circle cx="250" cy="250" r="48" fill="#0A1D30" stroke="#D8B66A" strokeWidth="1.5" />
      <circle cx="250" cy="250" r="58" fill="none" stroke="#5AA9E6" strokeOpacity=".5" strokeDasharray="1 4" />
      <foreignObject x="226" y="226" width="48" height="48"><Logo className="h-12 w-12" /></foreignObject>
    </svg>
  );
};

export const Platforms = () => {
  const [paused, setPaused] = useState(false);
  return (
    <section className="on-dark relative overflow-hidden bg-[#0A1D30] grid-dark py-24 md:py-32 text-white" data-testid="platforms-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead dark eyebrow="Sectors & platforms" title={["Precision technology.", "Reliable drafting support."]} className="max-w-5xl"
          intro="Scalable survey CAD drafting for surveying, engineering, mapping and development teams — produced in your standards, on the platforms you already use." />
        <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div className="border border-white/10 bg-[#102A43]/60" data-testid="sectors-panel">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <p className="eyebrow text-[10px] text-[#D8B66A]">Sectors we support</p>
              <Link to="/get-started?trial=1" className="u-link font-mono text-[11px] uppercase tracking-[0.14em] text-white" data-testid="sectors-trial-link">Start free trial →</Link>
            </div>
            <ul className="grid sm:grid-cols-2">
              {SECTORS.map(([Icon, t, d], i) => (
                <Reveal as="li" key={t} delay={i * 0.06} className="border-b border-white/10 p-6 sm:[&:nth-child(odd)]:border-r" data-testid={`sector-${i + 1}`}>
                  <span className="grid h-11 w-11 place-items-center border border-white/15 text-[#5AA9E6]"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display text-lg font-bold">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#c3d0dd]">{d}</p>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="relative" data-testid="orbit-panel">
            <button type="button" onClick={() => setPaused((p) => !p)} aria-pressed={paused} data-testid="orbit-pause-toggle"
              className="mb-3 ml-auto flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#9fb3c8] hover:text-white">
              <span className={`relative h-4 w-8 border border-white/30 ${paused ? "bg-[#D8B66A]/30" : "bg-white/5"}`}><span className={`absolute top-0.5 h-2.5 w-2.5 bg-[#D8B66A] transition-transform duration-300 ${paused ? "translate-x-[18px]" : "translate-x-0.5"}`} /></span>
              {paused ? "Resume motion" : "Pause motion"}
            </button>
            <div className="mx-auto aspect-square w-full max-w-[560px]"><Orbit paused={paused} /></div>
            <div className="mt-6 grid gap-3 text-[11px]">
              <div className="flex flex-wrap items-center gap-2"><span className="mr-2 font-mono uppercase tracking-[0.16em] text-[#D8B66A]">Software</span>{SOFTWARE.map((s) => <span key={s} className="border border-white/15 px-2.5 py-1 font-mono text-[#dbe6f1]">{s}</span>)}</div>
              <div className="flex flex-wrap items-center gap-2"><span className="mr-2 font-mono uppercase tracking-[0.16em] text-[#D8B66A]">Survey services</span>{CATEGORIES.map((c) => <Link key={c.id} to={`/services?cat=${c.id}`} className="border border-white/15 px-2.5 py-1 font-mono text-[#dbe6f1] transition-colors hover:border-[#5AA9E6] hover:text-white">{c.code}. {c.label}</Link>)}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Process = () => (
  <section className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="process-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-end">
        <SectionHead eyebrow="How we operate" title={["From field data", "to final drawing."]} />
        <Reveal delay={0.1} className="lg:pl-10">
          <p className="text-base md:text-lg leading-relaxed text-[#526579]">Every drawing is checked by its drafter and then reviewed again by a second team member — so your time goes into sealing, not fixing.</p>
          <Btn to="/get-started?trial=1" className="mt-7" testid="process-trial-button">Try it on a real job</Btn>
        </Reveal>
      </div>
      <ol className="relative mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-6 lg:gap-6" data-testid="process-stages">
        <span aria-hidden className="absolute left-7 right-7 top-7 hidden h-px bg-[#102A43]/15 lg:block" />
        {STAGES.map(([Icon, t, d], i) => (
          <Reveal as="li" key={t} delay={i * 0.07} className="relative" data-testid={`stage-${i + 1}`}>
            <span className={`relative z-10 grid h-14 w-14 place-items-center rounded-full border-2 bg-white transition-colors duration-300 ${i === 4 ? "border-[#D8B66A] bg-[#D8B66A] text-[#102A43]" : "border-[#1769AA]/40 text-[#1769AA]"}`}><Icon className="h-5 w-5" /></span>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1769AA]">Stage {String(i + 1).padStart(2, "0")}</p>
            <h3 className="mt-2 font-display text-lg font-bold leading-snug text-[#102A43]">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#526579]">{d}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const CLUSTERS = [
  ["Land survey drafting", "/services?cat=property", "Boundary survey drafting, ALTA/NSPS land title survey drafting, cadastral mapping, plot plans, subdivision plats and easement / right-of-way exhibits."],
  ["Topographic & terrain", "/services?cat=topo", "Topographic survey CAD drafting, contour mapping, spot level and elevation drawings, digital terrain models and existing ground plans."],
  ["CAD & data conversion", "/services/survey-data-to-cad-conversion", "Total station and GNSS data processing, point file to CAD, PDF to DWG, survey sketch digitisation and CAD redrafting."],
  ["GIS, drone & LiDAR", "/services?cat=specialized", "Drone and UAV survey mapping, LiDAR and point cloud to CAD conversion, scan-to-CAD, GIS mapping and shapefile to CAD."],
  ["Civil & infrastructure", "/services?cat=roads", "Road and highway survey CAD, utility and drainage drawings, construction layouts and as-built survey drafting."],
  ["Outsourcing & partnerships", "/outsourcing", "Survey drafting subcontracting, white-label CAD drafting and dedicated remote CAD teams for surveyors."],
];

export const Clusters = () => (
  <section className="bg-[#F3F6F8] grid-light py-20 md:py-28" data-testid="clusters-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <SectionHead eyebrow="Land survey CAD drafting & geospatial services" title={["Every survey drawing type,", "one drafting partner."]} intro="Survey CAD drafting services, land survey mapping, survey data processing and GIS mapping support — organised so you can find the exact drawing you need." />
      <ul className="mt-14 grid gap-px border border-[#102A43]/12 bg-[#102A43]/12 sm:grid-cols-2 lg:grid-cols-3">
        {CLUSTERS.map(([t, to, d], i) => (
          <Reveal as="li" key={t} delay={(i % 3) * 0.06}>
            <Link to={to} data-testid={`cluster-${i + 1}`} className="group flex h-full flex-col bg-white p-8 transition-colors hover:bg-[#102A43] hover:text-white">
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#D8B66A]">0{i + 1}</span>
              <h3 className="mt-3 font-display text-xl font-bold text-[#102A43] transition-colors group-hover:text-white">{t}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-[#526579] transition-colors group-hover:text-[#c3d0dd]">{d}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#1769AA] transition-colors group-hover:text-[#D8B66A]">Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const SOFTWARE_GROUPS = [
  ["CAD & drafting", ["AutoCAD", "Civil 3D", "Carlson Survey", "MicroStation", "BricsCAD", "AutoCAD Map 3D"]],
  ["GIS & mapping", ["QGIS", "ArcGIS Pro", "Global Mapper", "Google Earth Pro", "FME / data conversion"]],
  ["Survey & point cloud", ["Trimble Business Center", "Leica Infinity", "Autodesk ReCap", "CloudCompare", "Pix4D / Agisoft Metashape", "LandXML / PNEZD workflows"]],
];

export const Software = ({ dark = false }) => (
  <section className={`${dark ? "on-dark bg-[#0A1D30] grid-dark text-white" : "bg-white"} py-20 md:py-28`} data-testid="software-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <SectionHead dark={dark} eyebrow="Software we use" title={["The platforms behind", "every drawing."]} intro="We work natively in the software surveying and engineering firms already use, so deliverables open cleanly in your environment — no conversion surprises." />
      <div className="mt-14 grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3" style={!dark ? { borderColor: "rgba(16,42,67,.12)", background: "rgba(16,42,67,.12)" } : undefined}>
        {SOFTWARE_GROUPS.map(([g, items], i) => (
          <Reveal key={g} delay={i * 0.07} className={`${dark ? "bg-[#102A43]/60" : "bg-white"} p-8`} data-testid={`software-group-${i + 1}`}>
            <p className="eyebrow text-[10px] text-[#D8B66A]">{g}</p>
            <ul className="mt-5 flex flex-wrap gap-2">
              {items.map((t) => <li key={t} className={`border px-3 py-1.5 font-mono text-[11px] ${dark ? "border-white/15 text-[#dbe6f1]" : "border-[#102A43]/15 text-[#102A43]"}`}>{t}</li>)}
            </ul>
          </Reveal>
        ))}
      </div>
      <p className={`mt-6 text-xs ${dark ? "text-[#9fb3c8]" : "text-[#526579]"}`}>Product names are trademarks of their respective owners; listed to describe the file formats and workflows we support.</p>
    </div>
  </section>
);

export const TrialOffer = () => (
  <section className="relative overflow-hidden bg-[#D8B66A] py-14 text-[#102A43]" data-testid="trial-offer-section">
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
      <Reveal className="flex items-start gap-5">
        <span className="grid h-14 w-14 shrink-0 place-items-center border-2 border-[#102A43]"><Gift className="h-6 w-6" /></span>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em]">Try us free</p>
          <h2 className="mt-1 font-display text-2xl font-extrabold leading-tight sm:text-3xl">8 hours of drafting on a real project — no commitment.</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#102A43]/80">Send one live job with your data and template. We draft up to eight hours, you review the result in your own standards, and only then decide whether to continue.</p>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="shrink-0">
        <Link to="/get-started?trial=1" data-testid="trial-offer-button" className="group inline-flex items-center gap-3 bg-[#102A43] px-6 py-4 font-display text-[13px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1769AA]">
          Start the free trial <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);
